/**
 * Central registry deciding which reports widget slots use the cascading
 * field picker (`CascadeFieldPicker`) vs the flat one (`SearchableLightbox`).
 *
 * WHY a registry: "cascade vs flat is per-widget/per-slot configuration".
 * Widgets whose data selector is intentionally simple (Metric Card, Pie Chart)
 * stay flat for now, but flipping any slot to cascade later must be a one-line
 * change here — never a call-site edit. This is also the single place to make
 * the decision dynamic later (e.g. read platformConfigs / report settings)
 * without touching any ConfigForm section.
 *
 * Every ConfigForm data-source section resolves its picker prop through
 * `getPickerProp(...)` below — including the ones that currently resolve to
 * flat — so the wiring is uniform and future flips are trivial.
 */

export const PICKER_VARIANTS = {
  WidgetBarChart: { dimension1: "cascade", numberFields: "cascade", dimension2: "cascade" },
  WidgetHeatMapGrid: { dimension1: "cascade", dimension2: "cascade" },
  // dateField is flat: only ~one date field per template, so folders would be empty shells.
  WidgetLineChart: { dateField: "flat", breakdown: "cascade", numberFields: "cascade" },
  WidgetPieChart: { layerA: "cascade", layersBD: "cascade" },
  WidgetMetricCard: { field: "cascade", numberFields: "cascade" }, // simple selector UI — flat for now
  WidgetSankeyChart: { step0: "cascade", steps: "cascade", eventFlow: "cascade" },
};

/**
 * Resolve the `picker` prop to hand to <DataFieldInputSelector />.
 *
 * @returns { variant: 'cascade' } when the slot is configured for cascade, else
 *          `undefined` (→ flat SearchableLightbox, the untouched legacy path).
 *
 * The picker self-sources everything else it needs: template titles and the
 * Thematics/Attributes split gate are read from the react-query cache inside the
 * picker, and each sub-theme option carries its own `taxonomyRole`. So no
 * annotation metadata is threaded through call sites anymore.
 */
export const getPickerProp = ({ widgetComp, slot } = {}) => {
  const variant = PICKER_VARIANTS[widgetComp]?.[slot];
  if (variant !== "cascade") return undefined;
  return { variant: "cascade" };
};
